import { useTranslation } from "react-i18next";
import Field from "./Field";

// ─── Component ─────────────────────────────────────────────────

/**
 * MemberList
 *
 * Renders the "Add another guest" rows used by both flows. Each row is a
 * single name input plus a remove X button; an "+ Add another guest"
 * button sits below. Both flows drive name-only rows (the coded flow
 * internally attaches role/ageCategory — those defaults stay in the
 * hook, not here).
 *
 * Props:
 *   items     — { name: string }[]
 *   onUpdate  — (index, name) => void
 *   onAdd     — () => void
 *   onRemove  — (index) => void
 *   addLabel  — customize the "+ Add another guest" wording
 *   disabled  — disable all inputs/buttons
 *   compact   — when true, removes the outer bottom margin and renders
 *               the "+ Add" link as a small-caps subtle link (used when
 *               the list lives inside a section card footer)
 */
const MemberList = ({
  items = [],
  onUpdate,
  onAdd,
  onRemove,
  addLabel,
  disabled = false,
  compact = false,
}) => {
  const { t } = useTranslation();
  const resolvedAddLabel = addLabel || t("rsvp.addGuest");
  return (
  <div style={{ marginBottom: compact ? 0 : 24 }}>
    {items.map((member, index) => (
      <div
        key={index}
        className="anim-fade-in"
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 8,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Field
          type="text"
          placeholder={t("rsvp.guestNamePlaceholder")}
          value={member.name}
          onChange={(e) => onUpdate?.(index, e.target.value)}
          disabled={disabled}
          style={{ textAlign: "center", maxWidth: 300 }}
        />
        <button
          type="button"
          onClick={() => onRemove?.(index)}
          disabled={disabled}
          aria-label={t("rsvp.removeGuestAriaLabel")}
          style={{
            background: "none",
            border: "none",
            color: "var(--black)",
            cursor: disabled ? "not-allowed" : "pointer",
            fontSize: 20,
            lineHeight: 1,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          &times;
        </button>
      </div>
    ))}
    <div style={{ textAlign: compact ? "left" : "center", marginTop: 8 }}>
      <button
        type="button"
        onClick={onAdd}
        disabled={disabled}
        className="body-16"
        style={{
          background: "none",
          border: "none",
          color: "var(--black)",
          borderBottom: compact ? "none" : "1px solid var(--black)",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : compact ? 0.65 : 0.8,
          padding: 0,
          fontSize: compact ? 13 : undefined,
          letterSpacing: compact ? 0.3 : undefined,
          textTransform: compact ? "uppercase" : undefined,
        }}
      >
        {resolvedAddLabel}
      </button>
    </div>
  </div>
  );
};

export default MemberList;
